import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import axios from "axios";
import { checkPermissionByRoles } from "../utils/checkPermission";
import { getNextAvailableTab } from "../utils/getNextAvailableTab";
import { useSelector, useDispatch } from "react-redux";
import { clearUser, updateUser } from "../redux/users/usersSlice";
import ProviderAuthLayout from "../components/auth/ProviderAuthLayout";

const UpdatePasswordBusiness = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const user = useSelector((state) => state?.user);
  const [nextTab, setNextTab] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const validatePassword = (value) => {
    const re =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return re.test(value);
  };

  useEffect(() => {
    setNextTab(getNextAvailableTab(user, checkPermissionByRoles));
  }, [user]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!validatePassword(password)) {
      setErrorMessage("Password does not meet the required criteria.");
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const businessId = user.user?.businessId;
      const authToken = user.user?.token;

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/update-password-business`,
        {
          businessId,
          password,
          providerId: user.user.providerId,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (response.data.success) {
        const isBypassRole =
          user?.user?.role === "careManager" ||
          user?.user?.role === "psychiatricConsultant" ||
          user?.user?.providerType === "Care Manager" ||
          user?.user?.providerType === "Psychiatric Consultant";

        if (isBypassRole) {
          try {
            const activateId = user?.user?._id || user?.user?.providerId;
            await axios.put(
              `${process.env.REACT_APP_BASE_URL}/specialist/activateprovider/${activateId}`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${authToken}`,
                },
              }
            );
          } catch (err) {
            console.error("Failed to activate provider:", err);
          }
        }

        setSuccessMessage("Password reset successfully.");

        if (user?.user) {
          dispatch(
            updateUser({
              ...user.user,
              is_first_login: false,
            })
          );
        }

        setTimeout(() => {
          const bypass =
            user?.user?.role === "careManager" ||
            user?.user?.role === "psychiatricConsultant" ||
            user?.user?.providerType === "Care Manager" ||
            user?.user?.providerType === "Psychiatric Consultant";

          if (user?.user?.role === "provider" && !bypass) {
            navigate("/dashboard");
          } else if (bypass) {
            localStorage.clear();
            dispatch(clearUser());
            navigate("/login");
          } else {
            navigate(`/${nextTab}`);
          }
        }, 1000);
      } else {
        setErrorMessage("Failed to reset password. Please try again.");
      }
    } catch (error) {
      if (!navigator.onLine) {
        setErrorMessage(
          "Network connection lost. Please check your connection and try again."
        );
      } else {
        setErrorMessage(
          error.response?.data?.message ||
            "An error occurred. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <ProviderAuthLayout>
      <h1 className="provider-auth-title">Change password</h1>
      <p className="provider-auth-subtitle">Enter your new password.</p>

      <form className="provider-auth-form" onSubmit={handlePasswordSubmit}>
        <div className="provider-auth-field">
          <label className="provider-auth-label" htmlFor="first-login-password">
            New Password
          </label>
          <div className="provider-auth-password-wrap">
            <input
              id="first-login-password"
              className="provider-auth-input form-control"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="provider-auth-eye"
              aria-label={showPassword ? "Hide password" : "Show password"}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
            </button>
          </div>
        </div>

        <div className="provider-auth-field">
          <label
            className="provider-auth-label"
            htmlFor="first-login-confirm-password"
          >
            Confirm Password
          </label>
          <div className="provider-auth-password-wrap">
            <input
              id="first-login-confirm-password"
              className="provider-auth-input form-control"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="provider-auth-eye"
              aria-label={
                showConfirmPassword ? "Hide password" : "Show password"
              }
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <FaEyeSlash size={16} />
              ) : (
                <FaEye size={16} />
              )}
            </button>
          </div>
        </div>

        <p className="provider-auth-hint">
          Must be at least 8 characters long with one uppercase letter, one
          lowercase letter, a number, and a symbol.
        </p>

        {errorMessage ? (
          <p className="provider-auth-error" role="alert">
            {errorMessage}
          </p>
        ) : null}
        {successMessage ? (
          <p className="provider-auth-success">{successMessage}</p>
        ) : null}

        <button className="provider-auth-btn" type="submit" disabled={loading}>
          {loading ? "Resetting..." : "Reset Password"}
        </button>
      </form>

      <p className="provider-auth-footer">
        <Link className="provider-auth-link" to="/login">
          Back to Sign In
        </Link>
      </p>
    </ProviderAuthLayout>
  );
};

export default UpdatePasswordBusiness;
